import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { getTranslation } from '../../locales';
import {
  ModalOverlay,
  ModalContent,
  CloseButton,
  ModalTitle,
  Form,
  FormGroup,
  Label,
  Input,
  PackageInfo,
  PackageName,
  PackageLabel,
  ButtonContainer,
  SubmitButton,
  CancelButton,
  SuccessMessage,
  ErrorMessage,
  FieldError
} from './PurchaseRequestModal.styles';
import { purchaseService } from '../../services/purchaseService';

const PurchaseRequestModal = ({ isOpen, onClose, packageName }) => {
  const { language } = useLanguage();
  const t = getTranslation(language);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [apiMessage, setApiMessage] = useState({ text: '', type: '' });

  const [formData, setFormData] = useState({
    pacote: packageName || '',
    nome: '',
    telefone: '',
    email: ''
  });

  // Atualiza o pacote quando o modal abre
  useEffect(() => {
    if (packageName) {
      setFormData(prev => ({
        ...prev,
        pacote: packageName
      }));
    }
  }, [packageName]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Limpa erro do campo quando usuário começa a digitar
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Máscara de telefone brasileiro
  const handlePhoneChange = (e) => {
    let value = e.target.value.replace(/\D/g, '');

    if (value.length <= 11) {
      value = value.replace(/^(\d{2})(\d)/g, '($1) $2');
      value = value.replace(/(\d)(\d{4})$/, '$1-$2');

      setFormData(prev => ({
        ...prev,
        telefone: value
      }));
    }

    // Limpa erro do campo
    if (errors.telefone) {
      setErrors(prev => ({
        ...prev,
        telefone: ''
      }));
    }
  };

  // Validação de campos
  const validateField = (name, value) => {
    let error = '';

    switch (name) {
      case 'nome':
        if (!value || value.trim().length < 3) {
          error = t.purchaseModal.errors.nameLength;
        }
        break;

      case 'telefone':
        // Validação mais flexível para telefone brasileiro
        const phoneValue = value.replace(/\D/g, '');
        if (!phoneValue || phoneValue.length < 10 || phoneValue.length > 11) {
          error = t.purchaseModal.errors.phoneLength;
        }
        break;

      case 'email':
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!value || !emailRegex.test(value)) {
          error = t.purchaseModal.errors.emailInvalid;
        }
        break;

      default:
        break;
    }

    return error;
  };

  // Validação completa do formulário
  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    Object.keys(formData).forEach(field => {
      // Pula a validação do campo pacote pois ele é apenas informativo
      if (field === 'pacote') return;

      const error = validateField(field, formData[field]);
      if (error) {
        newErrors[field] = error;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  // Função para normalizar telefone brasileiro
  const normalizaTelefoneBR = (input) => {
    let d = String(input).replace(/\D+/g, "");
    // Se vier com DDI (55...), remova
    if (d.startsWith("55") && (d.length === 12 || d.length === 13)) d = d.slice(2);
    // Agora d deve ter 10 (fixo) ou 11 (celular) dígitos no BR
    return d;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setApiMessage({
        text: t.purchaseModal.errors.formErrors,
        type: 'error'
      });
      return;
    }

    setIsSubmitting(true);
    setApiMessage({ text: '', type: '' });

    try {
      const payload = {
        pacote: formData.pacote,
        nome: formData.nome.trim(),
        telefone: normalizaTelefoneBR(formData.telefone),
        email: formData.email.trim()
      };

      // Debug: log do payload para verificar o formato
      console.log('Payload sendo enviado:', payload);

      // Substituído o fetch por createPurchaseRequest
      const result = await purchaseService.createPurchaseRequest(payload);

      setIsSuccess(true);
      setApiMessage({
        text: t.purchaseModal.success,
        type: 'success'
      });

      // Fechar modal após 3 segundos
      setTimeout(() => {
        onClose();
        resetForm();
      }, 3000);

    } catch (error) {
      console.error('Erro ao enviar solicitação:', error);

      // Tratamento de erro mais robusto
      setApiMessage({
        text:
          error.response?.data?.error ||
          error.response?.data?.detail ||
          t.purchaseModal.error,
        type: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      pacote: packageName || '',
      nome: '',
      telefone: '',
      email: ''
    });
    setErrors({});
    setIsSuccess(false);
    setApiMessage({ text: '', type: '' });
  };

  const handleCancel = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={handleCancel}>&times;</CloseButton>

        <ModalTitle>{t.purchaseModal.title}</ModalTitle>

        {isSuccess ? (
          <SuccessMessage>
            ✅ {apiMessage.text}
          </SuccessMessage>
        ) : (
          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <Label htmlFor="pacote">{t.purchaseModal.packageLabel}</Label>
              <PackageInfo>
                <PackageName>{formData.pacote}</PackageName>
                <PackageLabel>{t.purchaseModal.verifyPackage}</PackageLabel>
              </PackageInfo>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="nome">{t.purchaseModal.fullName} {t.purchaseModal.required}</Label>
              <Input
                type="text"
                id="nome"
                name="nome"
                value={formData.nome}
                onChange={handleInputChange}
                placeholder={t.purchaseModal.namePlaceholder}
                required
                className={errors.nome ? 'error' : ''}
              />
              {errors.nome && <FieldError>{errors.nome}</FieldError>}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="telefone">{t.purchaseModal.phone} {t.purchaseModal.required}</Label>
              <Input
                type="tel"
                id="telefone"
                name="telefone"
                value={formData.telefone}
                onChange={handlePhoneChange}
                placeholder={t.purchaseModal.phonePlaceholder}
                required
                className={errors.telefone ? 'error' : ''}
              />
              {errors.telefone && <FieldError>{errors.telefone}</FieldError>}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="email">{t.purchaseModal.email} {t.purchaseModal.required}</Label>
              <Input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder={t.purchaseModal.emailPlaceholder}
                required
                className={errors.email ? 'error' : ''}
              />
              {errors.email && <FieldError>{errors.email}</FieldError>}
            </FormGroup>

            {apiMessage.text && (
              <div style={{ marginBottom: '1rem' }}>
                {apiMessage.type === 'error' ? (
                  <ErrorMessage>{apiMessage.text}</ErrorMessage>
                ) : (
                  <SuccessMessage>{apiMessage.text}</SuccessMessage>
                )}
              </div>
            )}

            <ButtonContainer>
              <CancelButton type="button" onClick={handleCancel}>
                {t.purchaseModal.cancel}
              </CancelButton>
              <SubmitButton type="submit" disabled={isSubmitting}>
                {isSubmitting ? t.purchaseModal.submitting : t.purchaseModal.submit}
              </SubmitButton>
            </ButtonContainer>
          </Form>
        )}
      </ModalContent>
    </ModalOverlay>
  );
};

export default PurchaseRequestModal;
